import { useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type {
  ColDef,
  GridSizeChangedEvent,
  RowClickedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'

ModuleRegistry.registerModules([AllCommunityModule])

interface DataGridProps<TData> {
  rowData: TData[]
  columnDefs: ColDef<TData>[]
  onRowClicked?: (data: TData) => void
  loading?: boolean
  noRowsMessage?: string
  rowHeight?: number
  context?: Record<string, unknown>
  onSortChanged?: (
    sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>
  ) => void
}

export function DataGrid<TData>({
  rowData,
  columnDefs,
  onRowClicked,
  loading,
  noRowsMessage = 'No data found',
  rowHeight = 68,
  context,
  onSortChanged,
}: DataGridProps<TData>) {
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<TData>) => {
      if (event.data && onRowClicked) {
        onRowClicked(event.data)
      }
    },
    [onRowClicked]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent) => {
      if (onSortChanged) {
        const sortedCols = event.api
          .getColumnState()
          .filter(col => col.sort != null)
        onSortChanged(
          sortedCols.map(col => ({
            colId: col.colId,
            sort: col.sort as 'asc' | 'desc',
          }))
        )
      }
    },
    [onSortChanged]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<TData>
            rowData={rowData}
            columnDefs={columnDefs}
            context={context}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={rowHeight}
            headerHeight={48}
            loading={loading}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            onRowClicked={onRowClicked ? handleRowClicked : undefined}
            onSortChanged={onSortChanged ? handleSortChanged : undefined}
            overlayNoRowsTemplate={`<span>${noRowsMessage}</span>`}
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
            rowClass={onRowClicked ? 'cursor-pointer' : undefined}
          />
        </div>
      </div>
    </div>
  )
}
